import type { AjaxSettings } from './ajax';
import { post } from './ajax';
import type * as ApiType from '@/common/api-type';
import { env } from './env';
import { post as postWithShark } from './ajax-with-shark';
import { statHelper, Cid } from '../stat';
import { AjaxBids, AjaxRaptorBids } from '@/stat/tech-constants';
import { StatReportType } from '@/neo';
import type { MarketPriority } from './enum-type';
import { formatError } from './utils';

// 极速支付二次弹窗使用的actioninfo shark请求
function saveActioninfo(
  params: ApiType.ApiActioninfoParams
): Promise<ApiType.BaseType> {
  const requestStartTime = Date.now();
  statHelper.reportToLX(
    Cid.OneclickpayModal,
    AjaxBids.ActioninfoStart,
    {},
    StatReportType.SC
  );
  statHelper.reportToRaptor(AjaxRaptorBids.ActioninfoStart);
  // 和后端约定这个接口前端不处理任何业务报错，静默处理
  return postWithShark<ApiType.BaseType>('/cashier/actioninfo', params, {
    useShark: true,
  })
    .then((r) => {
      if (r.error) {
        throw new Error(formatError(r));
      }
      const reportParams = { duration: Date.now() - requestStartTime };
      statHelper.reportToLX(
        Cid.OneclickpayModal,
        AjaxBids.ActioninfoSucc,
        reportParams,
        StatReportType.SC
      );
      statHelper.reportToRaptor(AjaxRaptorBids.ActioninfoSucc, reportParams);
      return r;
    })
    .catch((e: Error | ApiType.BaseType) => {
      let errorCode: string | undefined = '';
      let errorMsg: string | undefined = '';
      if (e instanceof Error) {
        // axios 错误
        errorCode = e.message;
        errorMsg = e.message;
      } else {
        // 业务错误
        errorCode = e?.error?.code;
        errorMsg = e?.error?.message;
      }
      const reportParams = {
        error_code: errorCode,
        error_msg: errorMsg,
        duration: Date.now() - requestStartTime,
      };
      statHelper.reportToLX(
        Cid.OneclickpayModal,
        AjaxBids.ActioninfoFail,
        reportParams,
        StatReportType.SC
      );
      statHelper.reportToRaptor(Cid.OneclickpayModal, reportParams);
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw e;
    });
}

function getOneClickPayStatus(): Promise<ApiType.IOneClickPayResponse> {
  return post('/api/wallet/oneclickpay/detailnew', {
    mtPlanId: env.mtPlanId,
    supportHalfPage: env.isEhcTransparentPage,
    utmSource: env.utmSource,
  });
}

function apiCloseOneClickPay(data: {
  closeSource: number;
  reasons?: string;
}): Promise<ApiType.ICloseOneClickPay> {
  return post('/api/wallet/oneclickpay/closeoneclickpay', {
    ...data,
    mtPlanId: env.mtPlanId,
  });
}

function openOneClickPay(
  data: ApiType.IOpenOneClickRequest
): Promise<ApiType.IOpenOneClickPay> {
  return post('/api/wallet/oneclickpay/openoneclickpay', data);
}

// 接口文档 https://km.sankuai.com/page/660810843
function adjustUserQuota(
  adjustUserQuotaRequest: ApiType.IAdjustUserQuotaRequest
): Promise<ApiType.IAdjustUserQuotaResponse> {
  return post('/mpaysign/oneClickPay/adjustUserQuota', {
    ...adjustUserQuotaRequest,
    mtPlanId: env.mtPlanId,
  });
}

function getQuotaInfo(verifyToken: string): Promise<ApiType.QuotaInfo> {
  return post('/mpaysign/oneClickPay/getQuotaInfo', {
    mtPlanId: env.mtPlanId,
    verifyToken,
  });
}

// 查找用户顺序 signStatus 用来标识用户是否签约，只有标准代扣关注此字段，其他业务都默认传 1
function getDeductionOrderDetail(): Promise<ApiType.IDeductionOrderDetail> {
  return post('/api/wallet/oneclickpay/deductorderdetail', {
    mtPlanId: env.mtPlanId,
    signStatus: env.signStatus,
  });
}

// 保存顺序
function saveDeductionOrder(
  data: ApiType.ISaveDeductionRequest,
  settings?: AjaxSettings
): Promise<ApiType.IDeductOrderSave> {
  return post(
    '/api/wallet/oneclickpay/deductordersave',
    {
      ...data,
      signStatus: env.signStatus,
      mtPlanId: env.mtPlanId,
    },
    settings
  );
}

function getPersuadeResponse(): Promise<ApiType.PersuadeResponse> {
  return post('/api/wallet/oneclickpay/retain');
}

function dataClean(): Promise<void> {
  return post('/deductOrder/modifyRedDot');
}

function unifyOrder(
  mtPlanId: string,
  payTypeList: string,
  marketPriority: MarketPriority
): Promise<ApiType.BaseType> {
  return post('/deductOrder/unionDeductOrder', {
    mtPlanId,
    payTypeList,
    marketPriority,
  });
}

function guideUnifyOrder(): Promise<ApiType.GuideDeductionOrderResponse> {
  return post('/deductOrder/guideDeductOrder', {
    mtPlanId: env.mtPlanId,
  });
}

// 保存系统优先设置系统推荐策略或美支置顶
function saveDeductionStrategy(
  data: ApiType.DeductOrderSetStrategyParams
): Promise<ApiType.DeductOrderSetStrategyResponse> {
  return post(
    '/paycustomer/sign/deductOrder/setStrategy',
    {
      ...data,
    },
    { handleError: false }
  );
}
// h5场景下的actioninfo ajax请求
function actioninfoAjax(
  data: ApiType.ApiActioninfoParams
): Promise<ApiType.BaseType> {
  return post(
    '/cashier/actioninfo',
    {
      ...data,
    },
    { showToast: false }
  );
}

/** 高优接口 */
function topPaySubType(
  data: ApiType.TopPaySubTypeRequest
): Promise<ApiType.TopPaySubTypeRespose> {
  return post(
    '/deductOrder/topPaySubType',
    {
      ...data,
    },
    { showToast: false }
  );
}

/** 调整免验状态 https://km.sankuai.com/collabpage/2749770782 */
function adjustNoAuthStatus(
  data: ApiType.AdjustNoAuthStatusRequest
): Promise<ApiType.AdjustNoAuthStatusResponse> {
  return post(
    '/paycustomer/sign/oneclickpay/adjustNoAuthStatus',
    {
      ...data,
    },
    { showToast: false }
  );
}

export {
  getOneClickPayStatus,
  apiCloseOneClickPay,
  openOneClickPay,
  adjustUserQuota,
  getQuotaInfo,
  getDeductionOrderDetail,
  saveDeductionOrder,
  getPersuadeResponse,
  dataClean,
  unifyOrder,
  guideUnifyOrder,
  saveDeductionStrategy,
  saveActioninfo,
  actioninfoAjax,
  topPaySubType,
  adjustNoAuthStatus,
};
